import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  Users, 
  Share2, 
  ArrowLeft,
  Facebook,
  Twitter,
  Linkedin,
  Copy,
  CheckCircle2,
  ExternalLink,
  Tent,
  Award
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Use SITE_URL for display, but BACKEND_URL for OG shareable links
const SITE_URL = window.location.origin;
const SHAREABLE_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const EventViewPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API_URL}/activities/event/${eventId}`);
      setEvent(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Parse date string as local date (not UTC) to avoid timezone issues
  // "2026-07-31" should always display as July 31, regardless of user's timezone
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  };

  const formatDate = (dateStr, endDateStr) => {
    const date = parseLocalDate(dateStr);
    if (!date) return 'Date TBD';
    
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    if (endDateStr && endDateStr !== dateStr) {
      const endDate = parseLocalDate(endDateStr);
      return `${date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', options)}`;
    }
    return date.toLocaleDateString('en-US', options);
  };

  const formatTime = (time, endTime) => {
    if (!time) return 'All Day';
    if (endTime) return `${time} - ${endTime}`;
    return time;
  };

  const eventUrl = `${SITE_URL}/events/${eventId}`;
  // Use the base URL (without /api) for OG shareable links
  const shareableUrl = `${SHAREABLE_BASE_URL}/api/og/events/${eventId}`;
  const eventImage = event?.image_url || event?.association_details?.image_url || 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1200';
  const eventTitle = event?.title || 'CCA Event';
  const eventDescription = event?.description || 'Join us for this exciting cultural event organized by CCA Montreal.';

  // Fallback copy function for when Clipboard API is blocked
  const copyToClipboard = async (text) => {
    // Try modern Clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
      }
    }
    
    // Fallback: Create a temporary textarea element
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful;
    } catch (err) {
      document.body.removeChild(textArea);
      return false;
    }
  };

  const handleCopyLink = async () => {
    const success = await copyToClipboard(shareableUrl);
    if (success) {
      setCopied(true);
      toast.success('Shareable link copied! This link shows a preview when shared.');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Could not copy link. Please select and copy manually.');
    }
  };

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareableUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareableUrl)}&text=${encodeURIComponent(eventTitle)}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareableUrl)}&title=${encodeURIComponent(eventTitle)}&summary=${encodeURIComponent(eventDescription)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${eventTitle} - ${shareableUrl}`)}`
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cameroon-green"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-slate-900">
        <Navbar variant="dark" />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 rounded-full bg-cameroon-red/20 flex items-center justify-center mx-auto mb-6">
            <Calendar className="w-10 h-10 text-cameroon-red" />
          </div>
          <h1 className="font-serif text-3xl font-bold text-white mb-4">Event Not Found</h1>
          <p className="text-white/60 mb-8">This event may have been removed or is not yet approved.</p>
          <Button onClick={() => window.location.href = '/calendar'} className="bg-cameroon-green hover:bg-cameroon-green/90">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Calendar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* OG Meta Tags for Social Sharing */}
      <Helmet>
        <title>{eventTitle} | CCA Montreal Events</title>
        <meta name="description" content={eventDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="event" />
        <meta property="og:url" content={eventUrl} />
        <meta property="og:title" content={eventTitle} />
        <meta property="og:description" content={eventDescription} />
        <meta property="og:image" content={eventImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="CCA Montreal" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={eventUrl} />
        <meta name="twitter:title" content={eventTitle} />
        <meta name="twitter:description" content={eventDescription} />
        <meta name="twitter:image" content={eventImage} />
        
        {/* Event specific */}
        <meta property="event:start_time" content={event?.date} />
        {event?.end_date && <meta property="event:end_time" content={event.end_date} />}
        {event?.location && <meta property="event:location" content={event.location} />}
      </Helmet>

      <div className="min-h-screen bg-slate-900">
        <Navbar variant="dark" />
        
        {/* Hero Section with Event Image */}
        <div className="relative h-[50vh] min-h-[400px] overflow-hidden">
          <img 
            src={eventImage}
            alt={eventTitle}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent"></div>
          
          {/* Back Button */}
          <div className="absolute top-6 left-6">
            <Button 
              variant="outline" 
              onClick={() => window.location.href = '/calendar'}
              className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Calendar
            </Button>
          </div>
          
          {/* Category Badge */}
          <div className="absolute top-6 right-6">
            <Badge className="bg-cameroon-green text-white text-sm px-4 py-1">
              {event.category || 'Event'}
            </Badge>
          </div>
        </div>

        {/* Event Details */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-32 relative z-10 pb-16">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="glass border-white/10">
                <CardContent className="p-8">
                  <h1 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-6">
                    {eventTitle}
                  </h1>
                  
                  {/* Date & Time */}
                  <div className="flex flex-wrap gap-6 mb-8 pb-8 border-b border-white/10">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-lg bg-cameroon-green/20 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-6 h-6 text-cameroon-green" />
                      </div>
                      <div>
                        <p className="text-sm text-white/60 mb-1">Date</p>
                        <p className="text-white font-medium">{formatDate(event.date, event.end_date)}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-lg bg-cameroon-red/20 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-6 h-6 text-cameroon-red" />
                      </div>
                      <div>
                        <p className="text-sm text-white/60 mb-1">Time</p>
                        <p className="text-white font-medium">{formatTime(event.time, event.end_time)}</p>
                      </div>
                    </div>
                    
                    {event.location && (
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-lg bg-cameroon-yellow/20 flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-6 h-6 text-cameroon-yellow" />
                        </div>
                        <div>
                          <p className="text-sm text-white/60 mb-1">Location</p>
                          <p className="text-white font-medium">{event.location}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Description */}
                  <div className="mb-8">
                    <h2 className="font-serif text-xl font-semibold text-white mb-4">About This Event</h2>
                    <p className="text-white/70 leading-relaxed whitespace-pre-line">
                      {event.description}
                    </p>
                  </div>
                  
                  {/* Notice */}
                  {event.notice && (
                    <div className="p-4 rounded-lg bg-cameroon-yellow/10 border border-cameroon-yellow/20">
                      <p className="text-white/80 text-sm">
                        <strong className="text-cameroon-yellow">Note:</strong> {event.notice}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Organizer Card */}
              <Card className="glass border-white/10">
                <CardContent className="p-6">
                  <h3 className="font-serif text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-cameroon-green" />
                    Organized By
                  </h3>
                  
                  {event.association_details ? (
                    <Link to={`/associations/${event.association_id}`} className="block group">
                      <div className="flex items-center gap-4">
                        <img 
                          src={event.association_details.image_url || 'https://images.unsplash.com/photo-1763256293604-326f823c3bb7?w=100'}
                          alt={event.association_details.name}
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                        <div>
                          <p className="text-white font-medium group-hover:text-cameroon-yellow transition-colors">
                            {event.association_details.short_name || event.association_name}
                          </p>
                          <p className="text-sm text-white/60">{event.association_details.name}</p>
                        </div>
                      </div>
                    </Link>
                  ) : (
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-lg bg-cameroon-green/20 flex items-center justify-center">
                        <Users className="w-8 h-8 text-cameroon-green" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{event.organization_name || event.submitter_name}</p>
                        <p className="text-sm text-white/60">{event.submitter_type === 'individual' ? 'Individual' : 'Organization'}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Share Card */}
              <Card className="glass border-white/10">
                <CardContent className="p-6">
                  <h3 className="font-serif text-lg font-semibold text-white mb-2 flex items-center gap-2">
                    <Share2 className="w-5 h-5 text-cameroon-red" />
                    Share This Event
                  </h3>
                  <p className="text-white/50 text-xs mb-4">
                    Use the buttons or link below for social media previews with image
                  </p>
                  
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <a 
                      href={shareLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full aspect-square rounded-lg bg-[#1877F2]/20 hover:bg-[#1877F2]/30 flex items-center justify-center transition-colors"
                      data-testid="share-facebook"
                    >
                      <Facebook className="w-6 h-6 text-[#1877F2]" />
                    </a>
                    <a 
                      href={shareLinks.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full aspect-square rounded-lg bg-[#1DA1F2]/20 hover:bg-[#1DA1F2]/30 flex items-center justify-center transition-colors"
                      data-testid="share-twitter"
                    >
                      <Twitter className="w-6 h-6 text-[#1DA1F2]" />
                    </a>
                    <a 
                      href={shareLinks.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full aspect-square rounded-lg bg-[#0A66C2]/20 hover:bg-[#0A66C2]/30 flex items-center justify-center transition-colors"
                      data-testid="share-linkedin"
                    >
                      <Linkedin className="w-6 h-6 text-[#0A66C2]" />
                    </a>
                    <a 
                      href={shareLinks.whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full aspect-square rounded-lg bg-[#25D366]/20 hover:bg-[#25D366]/30 flex items-center justify-center transition-colors"
                      data-testid="share-whatsapp"
                    >
                      <svg className="w-6 h-6 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </a>
                  </div>
                  
                  {/* Shareable Link with Preview */}
                  <div className="bg-cameroon-green/10 border border-cameroon-green/20 rounded-lg p-3 mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <ExternalLink className="w-4 h-4 text-cameroon-green" />
                      <span className="text-xs font-medium text-cameroon-green">Shareable Link (with preview)</span>
                    </div>
                    <div className="flex gap-2">
                      <input 
                        type="text" 
                        value={shareableUrl}
                        readOnly
                        className="flex-1 px-3 py-2 rounded-lg bg-slate-900/50 border border-white/10 text-white text-sm font-mono"
                        style={{ fontSize: '11px' }}
                      />
                      <Button 
                        variant="default" 
                        size="sm"
                        onClick={handleCopyLink}
                        className="bg-cameroon-green hover:bg-cameroon-green/90 text-white"
                        data-testid="copy-link-btn"
                      >
                        {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  
                  <p className="text-white/40 text-xs text-center">
                    Tip: Don't copy from browser address bar - use this link for image previews
                  </p>
                </CardContent>
              </Card>

              {/* Tent & Sponsor CTAs - only for tent-eligible events */}
              {event.tent_eligible && (
                <Card className="glass border-cameroon-yellow/30 bg-gradient-to-br from-cameroon-green/5 to-cameroon-yellow/5" data-testid="tent-sponsor-cta">
                  <CardContent className="p-6 space-y-3">
                    <h3 className="font-serif text-lg font-semibold text-white mb-1">Participate</h3>
                    <p className="text-white/60 text-sm">Showcase your business or sponsor this event.</p>
                    <Link to={`/events/${eventId}/reserve-tent`}>
                      <Button className="w-full bg-cameroon-green hover:bg-cameroon-green/90 mb-2" data-testid="reserve-tent-btn">
                        <Tent className="w-4 h-4 mr-2" />
                        Reserve a Tent/Canopy
                      </Button>
                    </Link>
                    <Link to={`/events/${eventId}/sponsor`}>
                      <Button className="w-full bg-cameroon-yellow hover:bg-cameroon-yellow/90 text-slate-900 mb-2" data-testid="become-sponsor-btn">
                        <Award className="w-4 h-4 mr-2" />
                        Become a Sponsor
                      </Button>
                    </Link>
                    <Link to={`/events/${eventId}/vendors`} className="block text-center">
                      <span className="text-sm text-cameroon-green hover:underline">View Vendors & Sponsors Directory</span>
                    </Link>
                  </CardContent>
                </Card>
              )}

              {/* CTA Card */}
              <Card className="glass border-cameroon-green/30 bg-cameroon-green/5">
                <CardContent className="p-6 text-center">
                  <h3 className="font-serif text-lg font-semibold text-white mb-2">Want to Book Your Event?</h3>
                  <p className="text-white/60 text-sm mb-4">
                    Reserve a date on the CCA Cultural Calendar for your association or community event.
                  </p>
                  <Button 
                    onClick={() => window.location.href = '/calendar'}
                    className="w-full bg-cameroon-green hover:bg-cameroon-green/90"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Book a Date
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="py-8 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-sm text-white/40">
              © {new Date().getFullYear()} Council of Cultural Associations Montreal. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default EventViewPage;
